import React, { useState, useEffect } from "react";
import NavBarEN from "./NavBarEN";
import NavBarMobileEN from "./NavBarMobileEN";
import { useMediaQuery } from "@uidotdev/usehooks";
import FormEN from "./FormEN";
import FooterEN from "./FooterEN";
import imageUvod from "../catalog/fig_iz_rada_objasnjen_hw.png";
import imageMetodologija from "../catalog/hw_methodology_belgrade.png";
import imageTrendoviDays from "../catalog/hw_days_fldmean_timeseries_trend.png";
import imageTrendoviEvents from "../catalog/hw_events_fldmean_timeseries_trend.png";
import imageTrendoviMeanmag from "../catalog/hw_meanmag_fldmean_timeseries_trend.png";
import imageUticaji from "../catalog/toplotni-talasi-uticaj.png";

import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";

import ReactHtmlParser from "html-react-parser";

// ===============================
// IMAGE LOADER (ISTO KAO SR VERZIJA)
// ===============================

const imagesProslost = require.context(
  "../toplotni-talasi-proslost-slike",
  false,
  /\.(png|jpe?g|svg)$/,
);

const imagesBuducnost = require.context(
  "../toplotni-talasi-buducnost",
  false,
  /\.(png|jpe?g|svg)$/,
);

// NetCDF fajlovi za prošlost
const ncProslost = require.context(
  "../fajlovi-toplotni-talasi-proslost",
  false,
  /\.nc$/,
);

// NetCDF fajlovi za budućnost
const ncBuducnost = require.context(
  "../fajlovi-toplotni-talasi-buducnost",
  false,
  /\.nc$/,
);

export default function ToplotniTalasiEN() {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isTabletOrLarger = useMediaQuery("only screen and (min-width: 769px)");
  const [activeTab, setActiveTab] = useState("uvod");
  const [activePodaciTab, setActivePodaciTab] = useState(
    "toplotni-talasi-u-proslosti",
  );

  const [indeks, setIndeks] = useState("hw_days");
  const [year, setYear] = useState("1950");
  const [yearBuducnost, setYearBuducnost] = useState("2041-2060");

  // -------------------------------------------------------
  // PROŠLOST: hw_days_1950.jpg, hw_events_2003.jpg, itd.
  // -------------------------------------------------------
  const getSelectedImageForHistory = () => {
    const imageName = `./${indeks}_${year}.jpg`;
    try {
      return imagesProslost(imageName);
    } catch (e) {
      console.error("Slika nije pronađena (prošlost):", imageName);
      return null;
    }
  };

  const getDownloadDataLinkForHistory = () => {
    const fileName = `./${indeks}_year_eobs_${year}.nc`;
    try {
      return ncProslost(fileName);
    } catch (e) {
      console.error("NetCDF nije pronađen (prošlost):", fileName);
      return null;
    }
  };

  // -------------------------------------------------------
  // BUDUĆNOST: hw_days_2041-2060_allpct.jpg, itd.
  // -------------------------------------------------------
  const getSelectedImageForProjections = () => {
    const imageName = `./${indeks}_${yearBuducnost}_allpct.jpg`;
    try {
      return imagesBuducnost(imageName);
    } catch (e) {
      console.error("Slika nije pronađena (budućnost):", imageName);
      return null;
    }
  };

  const getDownloadDataLinkForProjections = () => {
    const fileName = `./mod_${indeks}_${yearBuducnost}_hist_rcp45_rcp85_allpct.nc`;
    try {
      return ncBuducnost(fileName);
    } catch (e) {
      console.error("NetCDF nije pronađen (budućnost):", fileName);
      return null;
    }
  };

  const getTrendImage = () => {
    switch (indeks) {
      case "hw_days":
        return imageTrendoviDays;
      case "hw_events":
        return imageTrendoviEvents;
      case "hw_meanmag":
        return imageTrendoviMeanmag;
      default:
        return imageTrendoviDays;
    }
  };

  const data = [
    {
      label: "INTRODUCTION",
      value: "uvod",
      desc:
        `<h2>INTRODUCTION</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Heat waves are often defined as prolonged periods of unusually high temperatures relative to the expected climatic conditions for a given time and location. Although they are most commonly described as periods of extremely high air temperatures, their occurrence and development result from a combination of dynamical and thermodynamical factors operating on different spatial and temporal scales. They are influenced by rapid changes in atmospheric circulation, as well as slower processes within the climate system, such as processes in the upper layers of the ocean. In the long term, the key role in the increase of heat wave frequency, duration, and intensity is attributed to anthropogenic influences, including the rise in greenhouse gas concentrations and land-use changes.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Atmospheric circulation is the main driver of heat waves, whereby in mid-latitudes they often arise due to persistent blocking anticyclones that allow the retention of warm air and enhanced warming over a region. Three main mechanisms contributing to heat wave development are adiabatic warming due to subsidence, enhanced radiative heating due to reduced cloud cover, and horizontal advection of warm air. At the regional level, factors such as aerosols, land-use changes, and land–atmosphere interactions (e.g. reduced evaporation when soil is dry, leading to more energy being converted into sensible heat) can further intensify the magnitude and duration of heat waves. In urban areas, an additional factor is heat storage in materials such as concrete and reduced nocturnal radiative cooling efficiency, leading to higher nighttime temperatures and increased heat stress.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    The figure is taken from Barriopedro et al. (2023) and illustrates some of the drivers of heat waves and how they operate through interconnected processes across different spatial and temporal scales.
  </p>` +
        `<p style="color: #000;">
    Barriopedro, D., García‐Herrera, R., Ordóñez, C., Miralles, D.G. and Salcedo‐Sanz, S., 2023. Heat waves: Physical understanding and scientific challenges. Reviews of Geophysics, 61(2), p.e2022RG000780.
  </p>`,
      image: `<img src="${imageUvod}" alt="Heat Waves Introduction" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "METHODOLOGY",
      value: "metodologija",
      desc:
        `<h2>METHODOLOGY</h2>
  <p style="margin-bottom: 10px; color: #000;">
    There are many definitions of heat waves. According to the World Meteorological Organization (WMO), a heat wave is defined as “a marked warming of the air or an invasion of very hot air over a large area”. Different approaches to defining heat waves reflect their multidimensional nature, where the literature most commonly analyzes their frequency, duration, and intensity, while additional aspects such as timing or spatial extent are less frequently considered.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    One of the most commonly used approaches is based on percentile indices, which define thresholds of extreme values relative to local climatology. This approach allows comparisons between different regions and seasons, as it takes into account local climatic characteristics.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Heat waves are identified based on daily maximum air temperature (<strong>TX</strong>). The main threshold used is the <strong>90th percentile</strong> of daily maximum temperature defined relative to the 1961–1990 reference period for each calendar day of the year.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    For each grid point, it is determined whether TX exceeds the threshold, and a heat wave is defined as a sequence of at least five consecutive days during which the maximum temperature exceeds the <strong>90th percentile</strong>. For each identified event, the start date, end date, and duration are determined. The indicator <strong>hw_events</strong> represents the total number of heat waves per year, while <strong>hw_days</strong> represents the total number of days under heat wave conditions. The intensity of heat waves is quantified relative to the defined threshold. For each day belonging to a heat wave, the temperature exceedance relative to the threshold is calculated (TX − TX90p). At the event level, intensity is defined as the sum of daily exceedances over the duration of the event, and the indicator <strong>hw_meanmag</strong> is calculated as the mean intensity of events per year.
  </p>`,
      image: `<img src="${imageMetodologija}" alt="Heat Waves Methodology" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "TRENDS",
      value: "trendovi",
      desc:
        `<h2>TRENDS</h2>
  <p style="margin-bottom: 10px; color: #000;">
    At the global level, heat waves have become more frequent, longer-lasting, and more intense in recent decades. Across regions worldwide, extreme heat waves are occurring that would have been highly unlikely without the increase in greenhouse gas concentrations. Since the mid-20th century, an increasing trend has been observed in all mentioned characteristics of heat waves during the warm season. The most pronounced changes have been recorded in the Middle East and certain tropical regions, Europe, Australia, East Asia, and western North America.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    During the implementation of the EXTREMES project, spatiotemporal changes in heat waves in Serbia were analyzed using time series of indicators describing the number of heat waves (hw_events), total duration of heat waves (hw_days), and mean intensity (hw_meanmag) per year, calculated based on E-OBS data with a spatial resolution of 0.1° for the period 1950–2024. It was found that positive trends prevail for all indices, and their decadal values are presented in the graphs.
  </p>`,
      image: `
    <div style="display: flex; flex-direction: column; gap: 20px;">
      <img src="${imageTrendoviDays}" alt="Trend of heat wave days" style="max-width:100%;height:auto;">
      <img src="${imageTrendoviEvents}" alt="Trend of heat wave events" style="max-width:100%;height:auto;">
      <img src="${imageTrendoviMeanmag}" alt="Trend of mean heat wave intensity" style="max-width:100%;height:auto;">
    </div>
  `,
      desc2: `<p></p>`,
    },
    {
      label: "IMPACTS",
      value: "uticaji",
      desc:
        `<h2>IMPACTS</h2>
  <p style="margin-bottom: 10px; color: #000;">
    Heat waves are among the deadliest natural hazards, with impacts that extend far beyond the field of meteorology. As prolonged periods of unusually high temperatures relative to local climatic conditions, they have direct and indirect consequences on natural and social systems.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    The most pronounced impacts are on human health, where heat waves increase the risk of heat stroke, dehydration, and mortality, especially among vulnerable population groups. Estimates show that during the period 1998–2017 they were responsible for more than 166,000 deaths, while population exposure to extreme heat has significantly increased in recent decades (Pascaline & Rowena, 2018).
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    In addition to direct health effects, heat waves also affect the functioning of society and infrastructure. Increased demand for cooling leads to higher electricity consumption, which can cause overloads in power systems and supply disruptions. At the same time, high temperatures reduce outdoor labor productivity and can contribute to the disruption of social and economic stability.
  </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
    Impacts are also significant in the agriculture and ecosystem sectors. High temperatures cause heat stress in plants and animals, reduce yields, and increase the risk of land degradation. In natural ecosystems, heat waves can lead to forest decline, disruptions in ecosystem functioning, and an increased risk of wildfires.
  </p>` +
        `<p style="color: #000;">
    Pascaline, W. and Rowena, H., 2018. Economic losses, poverty and disaster 1998–2017. United Nations Office for Disaster Risk Reduction.
  </p>`,
      image: `<img src="${imageUticaji}" alt="Heat Wave Impacts" style="max-width:100%;height:auto;">`,
      desc2: `<p></p>`,
    },
    {
      label: "DATA",
      value: "podaci",
      tabs: [
        {
          label: "Heat Waves in the Past",
          value: "toplotni-talasi-u-proslosti",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <h3 className="text-lg font-semibold mb-4">
                  Heat Waves in the Past
                </h3>
                <p className="mb-4">
                  Select an index and a year to view the image:
                </p>

                <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  {/* Filter za indeks */}
                  <select
                    value={indeks}
                    onChange={(e) => setIndeks(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    <option value="hw_days">Number of days (hw_days)</option>
                    <option value="hw_events">
                      Number of events (hw_events)
                    </option>
                    <option value="hw_meanmag">
                      Mean magnitude (hw_meanmag)
                    </option>
                  </select>

                  {/* Filter za godinu */}
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    {Array.from({ length: 2024 - 1950 + 1 }, (_, index) => (
                      <option key={index} value={1950 + index}>
                        {1950 + index}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Deo za prikaz slike */}
                <div className="mt-6 w-full md:w-auto">
                  <h4 className="text-lg font-semibold">
                    Displayed image for {indeks}, {year}
                  </h4>
                  <img
                    src={getSelectedImageForHistory()}
                    alt={`Slika za ${indeks}, ${year}`}
                    className="w-full md:w-auto max-w-md mx-auto"
                  />
                </div>

                {/* Dugmad za preuzimanje */}
                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={getSelectedImageForHistory()}
                    download={`${indeks}_${year}.png`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download image
                  </a>
                  <a
                    href={getDownloadDataLinkForHistory()}
                    download={`${indeks}_year_eobs_${year}.nc`}
                    className="bg-green-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download data
                  </a>
                </div>
              </div>

              {/* Deo za dodatni tekst */}
              <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                <h4 className="text-lg font-semibold">
                  HEAT WAVES IN THE PAST
                </h4>
                <p className="mt-4 text-black">
                  Here you can download the hw_events, hw_days, and hw_meanmag
                  indices, calculated for each year from 1950 to 2024. The
                  indices are computed using E-OBS data. Results can be
                  downloaded in graphical format or in numerical NetCDF format.
                </p>
                <p className="mt-4 text-black">
                  E-OBS data:
                  <br />
                  Cornes, R.C., van der Schrier, G., van den Besselaar, E.J.M.,
                  & Jones, P.D. (2018). An Ensemble Version of the E-OBS
                  Temperature and Precipitation Datasets. Journal of Geophysical
                  Research: Atmospheres, 123, 9391–9409.
                </p>
              </div>
            </div>
          ),
        },
        {
          label: "Future Projections",
          value: "projekcije-za-buducnost",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <h3 className="text-lg font-semibold mb-4">
                  Future Projections
                </h3>
                <p className="mb-4">
                  Select an index and a period to view the projection:
                </p>

                <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <select
                    value={indeks}
                    onChange={(e) => setIndeks(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    <option value="hw_days">Number of days (hw_days)</option>
                    <option value="hw_events">
                      Number of events (hw_events)
                    </option>
                    <option value="hw_meanmag">
                      Mean magnitude (hw_meanmag)
                    </option>
                  </select>

                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    <option value="2021-2040">2021-2040</option>
                    <option value="2041-2060">2041-2060</option>
                    <option value="2061-2080">2061-2080</option>
                    <option value="2081-2100">2081-2100</option>
                  </select>
                </div>

                {/* Deo za prikaz slike */}
                <div className="mt-6 w-full md:w-auto">
                  <h4 className="text-lg font-semibold">
                    Displayed projection for {indeks}, {year}
                  </h4>
                  <img
                    src={getSelectedImageForProjections()}
                    alt={`Projection for ${indeks}, ${year}`}
                    className="w-full md:w-auto max-w-md mx-auto"
                  />
                </div>

                {/* Dugmad za preuzimanje */}
                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={getSelectedImageForProjections()}
                    download={`${indeks}_${year}_allpct.png`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download projection
                  </a>
                  <a
                    href={getDownloadDataLinkForProjections()}
                    download={`mod_${indeks}_${year}_hist_rcp45_rcp85_allpct.nc`}
                    className="bg-green-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download data
                  </a>
                </div>
              </div>

              {/* Deo za dodatni tekst */}
              <div className="suseDeoManji w-full md:w-1/3 p-4 border-l">
                <h4 className="text-lg font-semibold">FUTURE PROJECTIONS</h4>
                <p className="mt-4 text-black">
                  Here you can download projections of hw_events, hw_days, and
                  hw_meanmag indices for four future time slices (2021–2040,
                  2041–2060, 2061–2080, 2081–2100). The indices are calculated
                  using results from eight different climate models obtained
                  from the Digital Climate Atlas of Serbia
                  https://atlas-klime.eko.gov.rs. The selected projections refer
                  to two emission scenarios, RCP8.5 and RCP4.5. Results can be
                  downloaded in graphical format or in numerical NetCDF format.
                </p>

                <p className="mt-4 mb-2 text-black">
                  List of regional climate models used:
                </p>
                <ul className="text-black text-md pl-1">
                  <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                  <li className="pb-1">- DMI-HIRHAM5</li>
                  <li className="pb-1">- KNMI-RACMO22E</li>
                  <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                  <li className="pb-1">- KNMI-RACMO22E</li>
                  <li className="pb-1">- CLMcom-CCLM4-8-17</li>
                  <li className="pb-1">- MPI-CSC-REMO2009</li>
                  <li>- MPI-CSC-REMO2009</li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <>
      {isTabletOrLarger && <NavBarEN />}
      {isSmallDevice && <NavBarMobileEN />}
      <div className="container mx-auto text-center min-h-screen md:mb-6">
        <Tabs value={activeTab} className="max-w-screen-xl mx-auto">
          <TabsHeader
            className="bg-transparent flex justify-center flex-wrap md:flex-nowrap mx-2 md:mx-0"
            indicatorProps={{
              className: "bg-gray-900/10 shadow-none !text-gray-900",
            }}
            onChange={(value) => setActiveTab(value)}
          >
            {data.map(({ label, value }) => (
              <Tab className="probaTaba px-2 py-1" key={value} value={value}>
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody>
            {data.map(({ value, tabs, desc, image, desc2 }) => (
              <TabPanel key={value} value={value}>
                {value === "podaci" ? (
                  <Tabs
                    value={activePodaciTab}
                    onChange={(val) => setActivePodaciTab(val)}
                  >
                    <TabsHeader className="bg-gray-200">
                      {tabs.map(({ label, value }) => (
                        <Tab key={value} value={value}>
                          {label}
                        </Tab>
                      ))}
                    </TabsHeader>
                    <TabsBody>
                      {tabs.map(({ value, content }) => (
                        <TabPanel key={value} value={value}>
                          {content}
                        </TabPanel>
                      ))}
                    </TabsBody>
                  </Tabs>
                ) : (
                  <>
                    {/* Prikaz za ostale glavne tabove kao što su Uvod, Metodologija itd. */}
                    <div className="suseDeo flex flex-col md:flex-row">
                      <div className="suseDeoVeci w-full md:w-3/5 p-4">
                        {image && ReactHtmlParser(image)}
                        {desc2 && ReactHtmlParser(desc2)}
                      </div>
                      <div className="suseDeoManji w-full md:w-2/5 p-4 bg-white mt-6 md:mt-0">
                        {desc && ReactHtmlParser(desc)}
                      </div>
                    </div>
                  </>
                )}
              </TabPanel>
            ))}
          </TabsBody>
        </Tabs>
      </div>
      <FormEN />
      <FooterEN />
    </>
  );
}
